/**
 * StatusColorPicker — small popover-grid for choosing a column color.
 *
 * Notion-style: a tiny circle trigger (already showing the current
 * color). Click → grid of 10 swatches. Click a swatch → onChange().
 * Closes the popover via the consumer's open-state.
 *
 * Self-contained; uses the existing shadcn DropdownMenu primitive
 * rather than introducing a new Popover surface (single moving part
 * in the tab-key order, no portal collisions).
 */
import { Check } from 'lucide-react';

import {
  colorTint,
  normaliseColor,
  STATUS_COLOR_LABELS,
  STATUS_COLORS,
  type StatusColor,
} from '../../lib/status-color';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface StatusColorPickerProps {
  value: string | null | undefined;
  onChange: (next: StatusColor) => void;
  /** Aria label for the trigger; falls back to "Change color". */
  ariaLabel?: string;
  /** Optional extra classes on the trigger circle. */
  triggerClassName?: string;
}

export function StatusColorPicker({
  value,
  onChange,
  ariaLabel = 'Change color',
  triggerClassName,
}: StatusColorPickerProps) {
  const current = normaliseColor(value);
  const tint = colorTint(current);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            'flex h-3.5 w-3.5 items-center justify-center rounded-full border border-border/60 transition-transform hover:scale-110',
            triggerClassName,
          )}
          style={{
            backgroundColor: current === 'default' ? 'transparent' : tint.foreground,
          }}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44 p-1">
        <DropdownMenuLabel className="px-2 py-1 text-[11px] uppercase tracking-wider text-muted-foreground">
          Color
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="grid grid-cols-5 gap-1 p-1">
          {STATUS_COLORS.map((color) => {
            const swatch = colorTint(color);
            const isSelected = current === color;
            return (
              <button
                key={color}
                type="button"
                onClick={() => onChange(color)}
                aria-label={STATUS_COLOR_LABELS[color]}
                title={STATUS_COLOR_LABELS[color]}
                className={cn(
                  'group flex h-6 w-6 items-center justify-center rounded-full border transition-transform hover:scale-110',
                  isSelected ? 'border-foreground/40' : 'border-border/60',
                )}
                style={{
                  backgroundColor: color === 'default' ? 'transparent' : swatch.foreground,
                }}
              >
                {isSelected && (
                  <Check
                    className="h-3 w-3"
                    style={{
                      color: color === 'default' ? 'hsl(var(--color-foreground))' : 'white',
                    }}
                    aria-hidden
                  />
                )}
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
